import { ReactNode, useEffect, useRef, useState } from "react";
import {
    Avatar,
    AvatarController,
    AvatarItemKey,
    ItemData,
    Sprite,
    UserData,
} from "@mythic/pixl";

// Context imports
import { useCustomization } from "../../contexts/CustomizationContext";
import { useUser } from "../../contexts/UserContext";
import { usePixl } from "../../contexts/PixlContext";

import "./SpriteCanvas.css";

const animations = [
    { name: "idleRight", col: 0, row: 0, frames: 4, displayName: "Idle" },
    { name: "walkRight", col: 0, row: 2, frames: 6, displayName: "Walk" },
    { name: "jumpRight", col: 0, row: 4, frames: 8, displayName: "Jump" },
    { name: "dieRight", col: 0, row: 6, frames: 8, displayName: "Die" },
    { name: "runRight", col: 0, row: 8, frames: 6, displayName: "Run" },
];

interface Props {
    children?: ReactNode;
}
export default function SpriteCanvas({ children }: Props) {
    const controllerRef = useRef<AvatarController | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);

    const [anim, setAnim] = useState(0);

    const { customization, font, banner } = useCustomization();
    const { user } = useUser();

    const { pixlDb, pixlApp } = usePixl();

    useEffect(() => {
        const root = rootRef.current;
        if (!root || !pixlApp || !user) return;

        pixlApp.attachTo(root);
        pixlApp.enableTest();
        pixlApp.render();

        return () => {
            pixlApp.detach();
        };
    }, [rootRef, pixlApp, user]);

    function filterUserData(ud: UserData) {
        if (!pixlDb || !customization) return ud;

        const loadOrder = [
            "effects",
            "back_acc",
            "body",
            "clothes",
            "head",
            "eyes",
            "mouth",
            "face_acc",
            "head_acc",
            "body_acc",
        ] as AvatarItemKey[];

        ud.avatar.loadOrder = loadOrder;
        const translated = pixlDb.translateAvatarData(ud.avatar);
        return pixlDb.filterUserData({
            userId: ud.userId,
            username: ud.username,
            avatar: translated,
        });
    }

    useEffect(() => {
        // If customization is not set then abort
        if (!pixlApp || !customization || !pixlDb) return;

        // Get the current controller
        const controller = controllerRef.current;

        // If controller exists, make sure the current render of
        // pixlApp contains the controller for rendering.
        if (controller) {
            // PixlApp already has the controller, no actions
            // are required.
            if (pixlApp.hasController(controller)) return;

            // PixlApp does not have the controller, add the
            // controller to the app.
            pixlApp.addController(controller);
            return;
        }
        const userData = {
            userId: user?.id ?? "",
            username: user?.displayName ?? "",
            avatar: {
                ...customization,
                font: font ?? { name: "Arial", size: 20, color: "#FFFFFF" },
                banner: banner ?? { id: "", name: "", colorMap: ["#000000"] },
            },
        } as UserData;

        const cleanData = filterUserData(userData);

        // Controller doesn't exist, create one
        const av = Avatar.fromData(cleanData);
        const avc = new AvatarController(
            cleanData.userId,
            cleanData.username,
            av,
        );
        if (!avc) return;

        pixlApp.addController(avc);

        avc.avatar.animator.addAnimations(animations);
        avc.avatar.animator.play("idleRight");

        avc.moveTo({ x: 128, y: 0 });
        controllerRef.current = avc;
    }, [pixlApp, customization]);

    useEffect(() => {
        const controller = controllerRef.current;

        if (!customization || !controller || !pixlDb) return;

        // Build avatar data
        const data: UserData = {
            userId: user?.id ?? "",
            username: user?.displayName ?? "",
            avatar: {
                ...JSON.parse(JSON.stringify(customization)),
                loadOrder: [
                    "effects",
                    "back_acc",
                    "body",
                    "clothes",
                    "head",
                    "eyes",
                    "mouth",
                    "face_acc",
                    "head_acc",
                    "body_acc",
                ],
            },
        };

        // Translate data
        const translated = pixlDb.translateAvatarData(data.avatar);
        const loadOrder = [
            "effects",
            "back_acc",
            "body",
            "clothes",
            "head",
            "eyes",
            "mouth",
            "face_acc",
            "head_acc",
            "body_acc",
        ] as AvatarItemKey[];

        // Create the filters
        // Get the data for the user's head
        const head = pixlDb.getItem("head", translated.head?.id);

        // Get the data for the user's body
        const body = pixlDb.getItem("body", translated.body?.id);

        // Create the head/body filter. This will be
        // used to only display items the user currently
        // has access to. (i.e. remove eyes/mouth with wiggler head)
        const filter = {
            requiredHeadStyle: head.style,
            requiredBodyStyle: body.style,
        };

        for (let i = 0; i < loadOrder.length; i++) {
            const idata = translated[loadOrder[i]] as ItemData;
            if (
                ((idata.requiredHeadStyle?.length ?? 0) > 0 && // If there are required head styles
                    !idata.requiredHeadStyle?.includes(
                        // And the current headstyle is not in the list
                        filter.requiredHeadStyle,
                    )) || // OR
                ((idata.requiredBodyStyle?.length ?? 0) > 0 && // If there are required body styles
                    !idata.requiredBodyStyle?.includes(
                        // And the current bodystyle is not in the list
                        filter.requiredBodyStyle,
                    ))
            ) {
                translated[loadOrder[i]] = { id: "" } as ItemData;
            }
        }

        // Build a new sprite
        const sprite = new Sprite(translated, undefined, undefined, () => {
            // On ready, set the sprite.
            controller.avatar.sprite = sprite;
        });
    }, [pixlApp, pixlDb, customization, controllerRef, user]);

    useEffect(() => {
        const controller = controllerRef.current;

        if (!controller || !pixlDb) return;

        const bannerData = pixlDb.getItem("banner", banner?.id ?? "");

        // If the returned bannerData is invalid
        if (!bannerData) {
            controller.avatar.banner = {
                id: "",
                name: "",
                colorMap: banner?.colorMap ?? [],
            };
            return;
        }

        controller.avatar.banner = {
            ...bannerData,
            colorMap: banner?.colorMap ?? [],
        };
    }, [banner]);

    useEffect(() => {
        const controller = controllerRef.current;
        if (!controller) return;
        controller.avatar.font = font ?? {
            name: "Arial",
            size: 20,
            color: "#FFFFFF",
        };
    }, [font]);

    useEffect(() => {
        const avc = controllerRef.current;
        if (!avc) return;

        const animation = animations[anim];
        if (!animation) return;

        avc.avatar.animator.play(animation.name);
    }, [anim]);

    return (
        <div className="sprite-canvas-wrapper">
            <div className="sprite-canvas" ref={rootRef}></div>
            <div className="sprite-canvas-children">
                <div className="animation-controls">
                    <button
                        onClick={() => {
                            const prevAnim = anim - 1;

                            // If anim goes below zero, go to the
                            // end of the list instead of negative
                            if (prevAnim < 0)
                                return setAnim(animations.length - 1);

                            setAnim(prevAnim);
                        }}
                    >
                        &lt;
                    </button>
                    <span>{animations[anim]?.displayName}</span>
                    <button
                        onClick={() => {
                            const nextAnim = anim + 1;

                            // If anim goes too high, go to the
                            // start of the list instead of out of range
                            if (nextAnim >= animations.length)
                                return setAnim(0);

                            setAnim(nextAnim);
                        }}
                    >
                        &gt;
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}
